/**
 * The integration catalogue: one row per vendor, shared by the API (which
 * builds the live provider) and the dashboard (which renders the hub).
 *
 * Adding a vendor used to mean editing a union type, a factory switch, a
 * dashboard array and the docs. Most of that work was never vendor-specific:
 * the long tail of integrations is one of four mechanisms, and only the
 * mechanism needs code.
 *
 *   webhook       POST our JSON to a URL the customer pastes in. Every tool
 *                 with an "incoming webhook" is this, and it needs no client.
 *   deeplink      A URL template. Nothing is sent anywhere — we build a link
 *                 from the session's own ids, so there are no credentials to
 *                 store and nothing to break when the vendor changes its API.
 *   email-intake  Open a ticket by emailing the desk's intake address. Every
 *                 help desk supports it, and it needs no OAuth app per vendor.
 *   api           A real client we wrote. Reserved for tools where the return
 *                 value matters — a ticket id and URL we can link back to.
 *
 * So a new vendor is usually a row in this file. A new *mechanism* is the only
 * thing that costs a provider class.
 *
 * It lives in `contracts` rather than in `integrations` because the dashboard
 * renders from it: the integrations package pulls in nodemailer and the vendor
 * clients, and importing that from a browser bundle takes the whole app down.
 * Contracts is the shared, dependency-free half both sides can hold.
 */
// ── shared field shapes ──────────────────────────────────────────────────────
const WEBHOOK_URL = (help) => [
    { name: 'webhookUrl', label: 'Webhook URL', secret: true, placeholder: 'https://…', help },
];
const LINK_TEMPLATE = (example) => [
    {
        name: 'linkUrlTemplate',
        label: 'Link template',
        placeholder: example,
        help: 'Placeholders: {sessionId} {userEmail} {anonymousId} {traceId} {startMs} {endMs}',
    },
    { name: 'label', label: 'Button label', optional: true, placeholder: 'Open in vendor' },
];
/** Replay tools ship a default URL; the override is for a non-standard tenant. */
const REPLAY_ADVANCED = [
    { name: 'searchUrlTemplate', label: 'Search URL template (advanced)', optional: true },
    { name: 'label', label: 'Button label', optional: true },
];
const EMAIL_INTAKE = [
    {
        name: 'to',
        label: 'Intake address',
        placeholder: 'support@yourcompany.zendesk.com',
        help: "The address your help desk turns into tickets. Find it under the desk's email/channel settings.",
    },
    { name: 'host', label: 'SMTP host', placeholder: 'smtp.sendgrid.net' },
    { name: 'port', label: 'Port', type: 'number', placeholder: '587', optional: true },
    { name: 'username', label: 'SMTP username', optional: true },
    { name: 'password', label: 'SMTP password', secret: true, optional: true },
    { name: 'from', label: 'From address', placeholder: 'replayops@yourcompany.com' },
];
// ── the catalogue ────────────────────────────────────────────────────────────
export const INTEGRATION_CATALOG = [
    // Alerting & chat
    {
        id: 'slack', name: 'Slack', category: 'Alerting & chat', mechanism: 'api',
        blurb: 'Alert a channel on errors and rage clicks', icon: '💬', capabilities: ['notify'],
        fields: WEBHOOK_URL('Slack → Apps → Incoming Webhooks.'), docs: 'slack',
    },
    {
        id: 'teams', name: 'Microsoft Teams', category: 'Alerting & chat', mechanism: 'api',
        blurb: 'Post session alerts to a Teams channel', icon: '👥', capabilities: ['notify'],
        fields: WEBHOOK_URL('Channel → Connectors → Incoming Webhook.'),
    },
    {
        id: 'discord', name: 'Discord', category: 'Alerting & chat', mechanism: 'webhook',
        blurb: 'Alert a Discord channel', icon: '🎮', capabilities: ['notify'],
        fields: WEBHOOK_URL('Channel → Edit → Integrations → Webhooks.'),
    },
    {
        id: 'google-chat', name: 'Google Chat', category: 'Alerting & chat', mechanism: 'webhook',
        blurb: 'Alert a Google Chat space', icon: '🗨️', capabilities: ['notify'],
        fields: WEBHOOK_URL('Space → Apps & integrations → Webhooks.'),
    },
    {
        id: 'email', name: 'Email', category: 'Alerting & chat', mechanism: 'api',
        blurb: 'Send alerts to a mailbox', icon: '✉️', capabilities: ['notify'],
        fields: [
            { name: 'host', label: 'SMTP host', placeholder: 'smtp.sendgrid.net' },
            { name: 'port', label: 'Port', type: 'number', placeholder: '587', optional: true },
            { name: 'username', label: 'Username', optional: true },
            { name: 'password', label: 'Password', secret: true, optional: true },
            { name: 'from', label: 'From', placeholder: 'replayops@yourcompany.com' },
            { name: 'to', label: 'To', placeholder: 'support@yourcompany.com' },
        ],
    },
    // Incident & on-call
    {
        id: 'pagerduty', name: 'PagerDuty', category: 'Incident & on-call', mechanism: 'api',
        blurb: 'Page the on-call when a signal spikes', icon: '🚨', capabilities: ['notify'],
        fields: [
            {
                name: 'routingKey', label: 'Integration key', secret: true,
                help: 'Service → Integrations → add an Events API v2 integration.',
            },
        ],
        docs: 'pagerduty',
    },
    {
        id: 'opsgenie', name: 'Opsgenie', category: 'Incident & on-call', mechanism: 'webhook',
        blurb: 'Raise an Opsgenie alert', icon: '🛎️', capabilities: ['notify'],
        fields: WEBHOOK_URL('Teams → Integrations → Webhook.'),
    },
    // Issue tracking
    {
        id: 'jira', name: 'Jira', category: 'Issue tracking', mechanism: 'api',
        oauth: true,
        blurb: 'Open a Jira issue with the replay attached', icon: '🧩', capabilities: ['issue'],
        fields: [
            { name: 'baseUrl', label: 'Site URL', placeholder: 'https://acme.atlassian.net', host: true },
            { name: 'email', label: 'Account email', placeholder: 'you@acme.com' },
            { name: 'apiToken', label: 'API token', secret: true, help: 'id.atlassian.com → Security → API tokens.' },
            {
                name: 'projectKey', label: 'Project', placeholder: 'SUP',
                options: { source: 'projects', dependsOn: ['baseUrl', 'email', 'apiToken'] },
            },
            {
                name: 'issueType', label: 'Issue type', optional: true, placeholder: 'Task',
                options: { source: 'issueTypes', dependsOn: ['baseUrl', 'email', 'apiToken', 'projectKey'] },
            },
        ],
        docs: 'jira',
    },
    {
        id: 'github', name: 'GitHub', category: 'Issue tracking', mechanism: 'api',
        oauth: true,
        blurb: 'Open an issue in a repository', icon: '🐙', capabilities: ['issue'],
        fields: [
            {
                name: 'token', label: 'Personal access token', secret: true,
                help: 'Classic token with the `repo` scope, or a fine-grained token with Issues: read and write.',
            },
            {
                name: 'owner', label: 'Owner', placeholder: 'acme',
                help: "GitHub's login, not a display name. Pick it once the token is in.",
                options: { source: 'owners', dependsOn: ['token'] },
            },
            {
                name: 'repo', label: 'Repository', placeholder: 'storefront',
                options: { source: 'repos', dependsOn: ['token', 'owner'], creatable: true },
            },
        ],
        docs: 'github',
    },
    {
        id: 'linear', name: 'Linear', category: 'Issue tracking', mechanism: 'api',
        oauth: true,
        blurb: 'File a Linear issue from a session', icon: '📐', capabilities: ['issue'],
        fields: [
            { name: 'apiKey', label: 'API key', secret: true, help: 'Linear → Settings → API → Personal API keys.' },
            { name: 'teamId', label: 'Team ID', help: 'Settings → Teams → the team → General.' },
        ],
        docs: 'linear',
    },
    {
        id: 'trello', name: 'Trello', category: 'Issue tracking', mechanism: 'api',
        blurb: 'Add a card to a Trello list', icon: '📋', capabilities: ['issue'],
        fields: [
            { name: 'apiKey', label: 'API key', secret: true, help: 'trello.com/power-ups/admin → your Power-Up → API key.' },
            { name: 'token', label: 'Token', secret: true },
            { name: 'listId', label: 'List ID', help: 'Open the board with `.json` appended and find the list id.' },
        ],
    },
    {
        id: 'azure-devops', name: 'Azure DevOps', category: 'Issue tracking', mechanism: 'webhook',
        blurb: 'Notify a pipeline or service hook', icon: '🔷', capabilities: ['notify'],
        fields: WEBHOOK_URL('Project settings → Service hooks → Web Hooks.'),
    },
    {
        id: 'gitlab', name: 'GitLab', category: 'Issue tracking', mechanism: 'webhook',
        blurb: 'Notify a GitLab webhook', icon: '🦊', capabilities: ['notify'],
        fields: WEBHOOK_URL('Project → Settings → Webhooks.'),
    },
    {
        id: 'asana', name: 'Asana', category: 'Issue tracking', mechanism: 'email-intake',
        blurb: 'Create a task by emailing a project', icon: '🎯', capabilities: ['issue'],
        fields: EMAIL_INTAKE,
    },
    {
        id: 'clickup', name: 'ClickUp', category: 'Issue tracking', mechanism: 'email-intake',
        blurb: 'Create a task by emailing a list', icon: '⬆️', capabilities: ['issue'],
        fields: EMAIL_INTAKE,
    },
    // Customer support
    {
        id: 'zendesk', name: 'Zendesk', category: 'Customer support', mechanism: 'api',
        blurb: 'Raise a Zendesk ticket with the session attached', icon: '🎫', capabilities: ['issue'],
        fields: [
            { name: 'subdomain', label: 'Subdomain', placeholder: 'acme', help: 'The acme in acme.zendesk.com.', host: true },
            { name: 'email', label: 'Agent email', placeholder: 'you@acme.com' },
            { name: 'apiToken', label: 'API token', secret: true, help: 'Admin → Apps and integrations → Zendesk API.' },
            { name: 'defaultEmail', label: 'Fallback requester', placeholder: 'support@acme.com',
                help: 'Used when the session has no identified visitor.' },
        ],
        docs: 'zendesk',
    },
    {
        id: 'freshdesk', name: 'Freshdesk', category: 'Customer support', mechanism: 'api',
        blurb: 'Raise a Freshdesk ticket', icon: '🧊', capabilities: ['issue'],
        fields: [
            { name: 'domain', label: 'Domain', placeholder: 'acme.freshdesk.com', host: true },
            { name: 'apiKey', label: 'API key', secret: true },
            { name: 'defaultEmail', label: 'Fallback requester', placeholder: 'support@acme.com' },
        ],
    },
    {
        id: 'servicenow', name: 'ServiceNow', category: 'Customer support', mechanism: 'api',
        blurb: 'Open an incident record', icon: '🧾', capabilities: ['issue'],
        fields: [
            { name: 'instance', label: 'Instance', placeholder: 'acme.service-now.com', host: true },
            { name: 'username', label: 'Username' },
            { name: 'password', label: 'Password', secret: true },
            { name: 'table', label: 'Table', optional: true, placeholder: 'incident' },
        ],
    },
    {
        id: 'intercom', name: 'Intercom', category: 'Customer support', mechanism: 'email-intake',
        blurb: 'Start a conversation by email', icon: '💠', capabilities: ['issue'],
        fields: EMAIL_INTAKE,
    },
    {
        id: 'helpscout', name: 'Help Scout', category: 'Customer support', mechanism: 'email-intake',
        blurb: 'Create a conversation by email', icon: '🐕', capabilities: ['issue'],
        fields: EMAIL_INTAKE,
    },
    {
        id: 'salesforce', name: 'Salesforce Service Cloud', category: 'Customer support',
        mechanism: 'email-intake', blurb: 'Open a case via Email-to-Case', icon: '☁️', capabilities: ['issue'],
        fields: EMAIL_INTAKE,
    },
    // Error monitoring
    {
        id: 'sentry', name: 'Sentry', category: 'Error monitoring', mechanism: 'webhook',
        blurb: 'Forward alerts to a Sentry webhook', icon: '🛡️', capabilities: ['notify', 'forward'],
        fields: WEBHOOK_URL('Settings → Integrations → Internal app with a webhook URL.'),
    },
    {
        id: 'rollbar', name: 'Rollbar', category: 'Error monitoring', mechanism: 'webhook',
        blurb: 'Forward alerts to Rollbar', icon: '📊', capabilities: ['notify', 'forward'],
        fields: WEBHOOK_URL('Project → Notifications → Webhook.'),
    },
    {
        id: 'bugsnag', name: 'Bugsnag', category: 'Error monitoring', mechanism: 'webhook',
        blurb: 'Forward alerts to Bugsnag', icon: '🐞', capabilities: ['notify', 'forward'],
        fields: WEBHOOK_URL('Project settings → Notifications → Webhook.'),
    },
    {
        id: 'crashlytics', name: 'Firebase Crashlytics', category: 'Error monitoring', mechanism: 'deeplink',
        blurb: 'Jump from a session to the Crashlytics console', icon: '🔥', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://console.firebase.google.com/project/acme/crashlytics'),
    },
    // APM & tracing
    {
        // The only integration that reads code back rather than linking out to it.
        // The SDK already stamps W3C trace context and carries the session id in
        // baggage; CoverOps's agents record which methods each of those requests
        // executed. So the key is already shared, and this is only the asking.
        id: 'coverops', name: 'CoverOps', category: 'APM & tracing', mechanism: 'api',
        blurb: 'Show which backend code each action in a session ran',
        icon: '🔎', capabilities: ['code'], apm: true,
        fields: [
            { name: 'baseUrl', label: 'API URL', placeholder: 'https://api.coverlens.ai' },
            // A read-only link token, not an agent token: this one only ever asks
            // what a session ran, and cannot report coverage.
            { name: 'token', label: 'Link token', placeholder: 'cov_…', secret: true },
        ],
        docs: 'apm',
    },
    {
        id: 'datadog', name: 'Datadog', category: 'APM & tracing', mechanism: 'deeplink',
        blurb: 'Open the session’s traces in Datadog', icon: '🐶', capabilities: ['link'], apm: true,
        fields: [
            { name: 'site', label: 'Site', optional: true, placeholder: 'datadoghq.com' },
            { name: 'traceUrlTemplate', label: 'Trace template', optional: true },
            { name: 'searchUrlTemplate', label: 'Search template', optional: true },
        ],
        docs: 'apm',
    },
    {
        id: 'newrelic', name: 'New Relic', category: 'APM & tracing', mechanism: 'deeplink',
        blurb: 'Open the session’s traces in New Relic', icon: '🟩', capabilities: ['link'], apm: true,
        fields: [
            { name: 'accountId', label: 'Account ID' },
            { name: 'traceUrlTemplate', label: 'Trace template', optional: true },
        ],
        docs: 'apm',
    },
    {
        id: 'appdynamics', name: 'AppDynamics', category: 'APM & tracing', mechanism: 'deeplink',
        blurb: 'Open the session’s traces in AppDynamics', icon: '🔺', capabilities: ['link'], apm: true,
        fields: [
            { name: 'controllerUrl', label: 'Controller URL', placeholder: 'https://acme.saas.appdynamics.com' },
            { name: 'traceUrlTemplate', label: 'Trace template', optional: true },
        ],
        docs: 'apm',
    },
    {
        id: 'dynatrace', name: 'Dynatrace', category: 'APM & tracing', mechanism: 'deeplink',
        blurb: 'Open the session’s traces in Dynatrace', icon: '🐝', capabilities: ['link'], apm: true,
        fields: LINK_TEMPLATE('https://abc12345.live.dynatrace.com/ui/diagnostictools/traces?traceId={traceId}'),
        docs: 'apm',
    },
    {
        id: 'honeycomb', name: 'Honeycomb', category: 'APM & tracing', mechanism: 'deeplink',
        blurb: 'Open the session’s traces in Honeycomb', icon: '🍯', capabilities: ['link'], apm: true,
        fields: LINK_TEMPLATE('https://ui.honeycomb.io/acme/environments/prod/trace?trace_id={traceId}'),
        docs: 'apm',
    },
    {
        id: 'otel', name: 'OpenTelemetry', category: 'APM & tracing', mechanism: 'deeplink',
        blurb: 'Link traces in any OTel-compatible backend', icon: '🔭', capabilities: ['link'], apm: true,
        fields: LINK_TEMPLATE('https://tracing.acme.com/trace/{traceId}'),
        docs: 'apm',
    },
    // Logs & search
    {
        id: 'splunk', name: 'Splunk', category: 'Logs & search', mechanism: 'deeplink',
        blurb: 'Search this session’s logs in Splunk', icon: '📗', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://acme.splunkcloud.com/en-US/app/search/search?q=search%20{sessionId}'),
        docs: 'logs',
    },
    {
        id: 'elastic', name: 'Elastic / Kibana', category: 'Logs & search', mechanism: 'deeplink',
        blurb: 'Search this session’s logs in Kibana', icon: '🔍', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://acme.kb.eu-west-1.aws.found.io/app/discover#/?_a=(query:(query:%27{sessionId}%27))'),
        docs: 'logs',
    },
    {
        id: 'sumologic', name: 'Sumo Logic', category: 'Logs & search', mechanism: 'deeplink',
        blurb: 'Search this session’s logs in Sumo Logic', icon: '🧮', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://service.sumologic.com/ui/#/search/@{startMs},{endMs}@{sessionId}'),
        docs: 'logs',
    },
    {
        id: 'grafana', name: 'Grafana', category: 'Logs & search', mechanism: 'deeplink',
        blurb: 'Open a Grafana dashboard scoped to the session', icon: '📈', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://acme.grafana.net/explore?left={"queries":[{"query":"{sessionId}"}]}'),
        docs: 'logs',
    },
    // Product analytics
    {
        id: 'segment', name: 'Segment', category: 'Product analytics', mechanism: 'webhook',
        blurb: 'Forward identify / track events', icon: '🔀', capabilities: ['forward'],
        fields: WEBHOOK_URL('Add a Webhooks destination in your Segment source.'),
    },
    {
        id: 'mixpanel', name: 'Mixpanel', category: 'Product analytics', mechanism: 'webhook',
        blurb: 'Forward events to Mixpanel', icon: '📶', capabilities: ['forward'],
        fields: WEBHOOK_URL('Any HTTP endpoint that ingests into your project.'),
    },
    {
        id: 'google-analytics', name: 'Google Analytics', category: 'Product analytics', mechanism: 'deeplink',
        blurb: 'Link a session to its GA4 user', icon: '📉', capabilities: ['link'],
        fields: [
            { name: 'propertyId', label: 'Property ID', placeholder: '123456789' },
            { name: 'measurementId', label: 'Measurement ID', optional: true, placeholder: 'G-XXXXXXX' },
            { name: 'linkUrlTemplate', label: 'Link template (advanced)', optional: true },
        ],
    },
    {
        id: 'amplitude', name: 'Amplitude', category: 'Product analytics', mechanism: 'deeplink',
        blurb: 'Open the same user in Amplitude', icon: '📡', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://analytics.amplitude.com/acme/search/{userEmail}'),
    },
    {
        id: 'heap', name: 'Heap', category: 'Product analytics', mechanism: 'deeplink',
        blurb: 'Open the same user in Heap', icon: '🧱', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://heapanalytics.com/app/env/123/users?identity={userEmail}'),
    },
    {
        id: 'pendo', name: 'Pendo', category: 'Product analytics', mechanism: 'deeplink',
        blurb: 'Open the same visitor in Pendo', icon: '🧭', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://app.pendo.io/visitors/{anonymousId}'),
    },
    {
        id: 'posthog', name: 'PostHog', category: 'Product analytics', mechanism: 'deeplink',
        blurb: 'Open the same person in PostHog', icon: '🦔', capabilities: ['link'],
        fields: LINK_TEMPLATE('https://eu.posthog.com/person/{userEmail}'),
    },
    // Feature flags & experiments
    {
        id: 'launchdarkly', name: 'LaunchDarkly', category: 'Feature flags & experiments',
        mechanism: 'webhook', blurb: 'Notify LaunchDarkly when a signal spikes', icon: '🚩',
        capabilities: ['notify'], fields: WEBHOOK_URL('Integrations → Webhooks.'),
    },
    {
        id: 'optimizely', name: 'Optimizely', category: 'Feature flags & experiments',
        mechanism: 'deeplink', blurb: 'Open the experiment this session ran in', icon: '🧪',
        capabilities: ['link'], fields: LINK_TEMPLATE('https://app.optimizely.com/v2/projects/123/results'),
    },
    // Voice of customer
    {
        id: 'qualtrics', name: 'Qualtrics', category: 'Voice of customer', mechanism: 'webhook',
        blurb: 'Send a signal to a Qualtrics workflow', icon: '📝', capabilities: ['notify', 'forward'],
        fields: WEBHOOK_URL('Workflows → add an inbound webhook event.'),
    },
    {
        id: 'delighted', name: 'Delighted', category: 'Voice of customer', mechanism: 'webhook',
        blurb: 'Send a signal to Delighted', icon: '😊', capabilities: ['notify', 'forward'],
        fields: WEBHOOK_URL('Any endpoint that accepts your survey trigger.'),
    },
    // Session replay
    {
        id: 'fullstory', name: 'FullStory', category: 'Session replay', mechanism: 'deeplink',
        blurb: 'Open the same user in FullStory', icon: '🎬', capabilities: ['link'],
        fields: [{ name: 'orgId', label: 'Org ID' }, ...REPLAY_ADVANCED],
    },
    {
        id: 'logrocket', name: 'LogRocket', category: 'Session replay', mechanism: 'deeplink',
        blurb: 'Open the same user in LogRocket', icon: '🚀', capabilities: ['link'],
        fields: [{ name: 'orgId', label: 'Org ID' }, { name: 'appId', label: 'App ID' }, ...REPLAY_ADVANCED],
    },
    {
        id: 'hotjar', name: 'Hotjar', category: 'Session replay', mechanism: 'deeplink',
        blurb: 'Open the same user in Hotjar', icon: '🔥', capabilities: ['link'],
        fields: [{ name: 'siteId', label: 'Site ID' }, ...REPLAY_ADVANCED],
    },
    {
        id: 'clarity', name: 'Microsoft Clarity', category: 'Session replay', mechanism: 'deeplink',
        blurb: 'Open the same user in Clarity', icon: '🔎', capabilities: ['link'],
        fields: [{ name: 'projectId', label: 'Project ID' }, ...REPLAY_ADVANCED],
    },
];
/** Section order in the hub, with the one-liner shown under each heading. */
export const CATEGORY_ORDER = [
    { name: 'Alerting & chat', desc: 'Get told when errors, rage clicks or alerts spike.' },
    { name: 'Incident & on-call', desc: 'Page whoever is on duty, with the replay attached.' },
    { name: 'Issue tracking', desc: 'Open a ticket from a session, deep-linked back to it.' },
    { name: 'Customer support', desc: 'Raise a support ticket without leaving the session.' },
    { name: 'Error monitoring', desc: 'Send crashes and error groups where your team watches them.' },
    { name: 'APM & tracing', desc: 'Jump from a session to the backend traces behind it.' },
    { name: 'Logs & search', desc: "Search a session's server-side logs by its id." },
    { name: 'Product analytics', desc: 'Follow the same person into your analytics tool.' },
    { name: 'Feature flags & experiments', desc: 'Tie a session to the flag or experiment it ran under.' },
    { name: 'Voice of customer', desc: 'Trigger a survey off what a session actually did.' },
    { name: 'Session replay', desc: 'Deep-link a user into another replay tool.' },
];
const BY_ID = new Map(INTEGRATION_CATALOG.map((e) => [e.id, e]));
export function catalogEntry(id) {
    return BY_ID.get(id);
}
/** Every field name a provider stores that must be encrypted at rest. */
export const SECRET_FIELDS = [
    ...new Set(INTEGRATION_CATALOG.flatMap((e) => e.fields.filter((f) => f.secret).map((f) => f.name))),
];
//# sourceMappingURL=integrationCatalog.js.map